import React, { useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Link,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import HelpButton from "../../../components/utilities/HelpButton";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import * as Yup from "yup";
import { Formik } from "formik";
import { globalVariables } from "../../../global/GlobalVariables";
import {
  BCPArrowLeft,
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import SquareIcon from "../../../components/icons/payment/SquareIcon";
import { StripeIcon } from "../../../components/icons/payment/StripeIcon";
import { useNavigate } from "react-router";
const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";

export default function OrganizationBanking(props) {
  const { organization, authContext } = props;

  const isAdmin = authContext?.authState?.identity?.isAdmin || false;

  const navigate = useNavigate();
  const confirm = useConfirm();

  const globalVars = globalVariables();

  const [readOnly, setReadOnly] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2, p: 2 }}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Link
              href="/organize/game-stores"
              color="link.main"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <BCPArrowLeft
                sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
              />{" "}
              Back to My Organizations
            </Link>
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
            >
              Banking{" "}
              {organization?.bankAccount?.label
                ? `- ${organization.bankAccount.label}`
                : "Details"}
            </Typography>
          </Grid>

          {organization?.chargeType === "authorized" && (
            <>
              {authContext?.authState?.identity?.isAdmin === true && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 2,
                  }}
                >
                  <Typography variant="h5">
                    <Button
                      color="primary"
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setReadOnly(!readOnly);
                      }}
                      endIcon={
                        readOnly ? (
                          <BCPLockKeyhole outlined />
                        ) : (
                          <BCPLockKeyholeOpen outlined />
                        )
                      }
                    >
                      {readOnly ? "Locked" : "Unlocked"}
                    </Button>
                  </Typography>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                <Formik
                  initialValues={{
                    storeId: organization?.storeId || "",
                    apiLoginKey:
                      organization?.authorizeNetKeys?.apiLoginKey || "",
                    transactionKey:
                      organization?.authorizeNetKeys?.transactionKey || "",
                    clientKey: organization?.authorizeNetKeys?.clientKey || "",
                  }}
                  validationSchema={Yup.object().shape({
                    storeId: Yup.string().required("storeId"),
                    apiLoginKey: Yup.string().required("Required"),
                    transactionKey: Yup.string().required("Required"),
                    clientKey: Yup.string().required("Required"),
                  })}
                  onSubmit={async (values) => {
                    setProcessing(true);
                    try {
                      var body = {
                        set: {
                          storeId: values.storeId,
                          authorizeNetKeys: {
                            apiLoginKey: values.apiLoginKey,
                            transactionKey: values.transactionKey,
                            clientKey: values.clientKey,
                          },
                        },
                      };

                      await postData(`/gamestores/${organization.id}`, {
                        body: body,
                      });
                      postNotification("success", "Organization updated");
                    } catch (err) {
                      postNotification("error", err);
                    }
                    setProcessing(false);
                    return;
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                  }) => (
                    <form onSubmit={handleSubmit} noValidate>
                      <Grid container sx={{ gap: 2 }}>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="h4">
                            Authorize.net API Credentials
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            p: 2,
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField
                                name="storeId"
                                label="Game Kastle Store Id"
                                value={values.storeId}
                                variant="outlined"
                                fullWidth
                                required
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.storeId && errors.storeId
                                )}
                                helperText={touched.storeId && errors.storeId}
                                disabled={readOnly}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                name="apiLoginKey"
                                label="API Login ID"
                                value={values.apiLoginKey}
                                variant="outlined"
                                fullWidth
                                required
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.apiLoginKey && errors.apiLoginKey
                                )}
                                helperText={
                                  touched.apiLoginKey && errors.apiLoginKey
                                }
                                disabled={readOnly}
                                InputProps={{
                                  sx: {
                                    cursor: "pointer",
                                    "& input": {
                                      cursor: "pointer",
                                    },
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <HelpButton title="This can be found at Authorize.net > Account > Settings > API Credentials & Keys" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                name="transactionKey"
                                label="Transaction Key"
                                value={values.transactionKey}
                                variant="outlined"
                                fullWidth
                                required
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.transactionKey &&
                                    errors.transactionKey
                                )}
                                helperText={
                                  touched.transactionKey &&
                                  errors.transactionKey
                                }
                                disabled={readOnly}
                                InputProps={{
                                  sx: {
                                    cursor: "pointer",
                                    "& input": {
                                      cursor: "pointer",
                                    },
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <HelpButton title="This can be generated at Authorize.net > Account > Settings > API Credentials & Keys > Create New Key(s)" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                name="clientKey"
                                label="Public Client Key"
                                value={values.clientKey}
                                variant="outlined"
                                fullWidth
                                required
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.clientKey && errors.clientKey
                                )}
                                helperText={
                                  touched.clientKey && errors.clientKey
                                }
                                disabled={readOnly}
                                InputProps={{
                                  sx: {
                                    cursor: "pointer",
                                    "& input": {
                                      cursor: "pointer",
                                    },
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <HelpButton title="This can be found at Authorize.net > Account > Settings > Public Client Key" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Button
                                ref={saveBoxRef}
                                fullWidth
                                color="success"
                                variant="contained"
                                type="submit"
                                disabled={readOnly}
                              >
                                Save
                              </Button>
                            </Grid>

                            {!saveBoxInView && (
                              <Box
                                sx={{
                                  position: "fixed",
                                  zIndex: 999,
                                  p: 2,
                                  bgcolor: "background.paper",
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                                  boxShadow: 3,
                                  bottom: 0,
                                  left: [0],
                                  width: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    fullWidth
                                    color="success"
                                    variant="contained"
                                    type="submit"
                                    disabled={readOnly}
                                  >
                                    Save
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Grid>
            </>
          )}

          {organization?.chargeType !== "authorized" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3" gutterBottom>
                  Want to sell tickets to your events?
                </Typography>

                {IS_BCP ? (
                  <Typography variant="body1" paragraph>
                    {globalVars?.site?.title} provide Event Organizers with a
                    simple way to register and process payments for events!
                  </Typography>
                ) : (
                  <Typography variant="body1" paragraph>
                    {globalVars?.site?.title} has partnered with Best Coast
                    Pairings to provide Event Organizers with a simple way to
                    register and process payments for events!
                  </Typography>
                )}
                <Typography variant="body1" paragraph>
                  {`This powerful tool is powered by the secure and trusted `}
                  <Link
                    href="https://stripe.com/"
                    target="_Blank"
                    underline="none"
                    sx={{ color: "link.stripe" }}
                  >
                    Stripe
                  </Link>{" "}
                  {isAdmin && (
                    <>
                      and{" "}
                      <Link
                        href="https://squareup.com/us/en"
                        target="_Blank"
                        underline="none"
                        sx={{ color: "link.square" }}
                      >
                        Square
                      </Link>
                    </>
                  )}
                  {` payment platform${isAdmin ? "s" : ""}. Event Organizers can attach a Business Account, Debit Card or personal checking account to their ${getEnvironmentVariable("APP_NAME") || "BCP"} account and once enabling online registration within Event Details, can set the price for  2-3 days of purchase. You can find more information on our `}
                  <Link
                    href="/pricing"
                    target="_Blank"
                    underline="none"
                    color="link"
                  >
                    Pricing
                  </Link>{" "}
                  {`page.`}
                </Typography>

                {organization?.chargeType === "square" && (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="warning" gutterBottom>
                      {`Tickets sold through Square cannot be refunded through BCP. If you need to issue a refund, please do so through your Square Dashboard.`}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {organization?.bankAccount?.id ? (
                <>
                  {organization?.chargeType === "stripe" ? (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: "link.stripe",
                          color: "#000",
                          "&:hover": {
                            backgroundColor: "primary.main",
                            color: "#fff",
                          },
                        }}
                        startIcon={<StripeIcon />}
                        onClick={async () => {
                          if (organization?.bankAccount?.id) {
                            setProcessing(true);
                            var link = await getData(
                              `/stripe/expresslink/${organization.bankAccount.id}`
                            );
                            window.open(link.url, "popup");
                            setProcessing(false);
                          }
                        }}
                      >
                        Update Stripe Bank Account
                      </Button>
                    </Grid>
                  ) : organization?.chargeType === "square" && isAdmin ? (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        sx={{ color: "#000" }}
                        fullWidth
                        startIcon={<SquareIcon />}
                        onClick={async () => {
                          setProcessing(true);

                          try {
                            navigate(
                              `/square-account?orgId=${organization.id}`
                            );
                          } catch (err) {
                            postNotification("error", err);
                          } finally {
                            setProcessing(false);
                          }
                        }}
                      >
                        Update Square Bank Account
                      </Button>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="h4" gutterBottom>
                        Your Organization is not set up to accept payments.
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {`Please contact your organization administrator to set up a payment processor.`}
                      </Typography>
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  {/* Square */}
                  {isAdmin && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        sx={{ color: "#000" }}
                        fullWidth
                        startIcon={<SquareIcon />}
                        onClick={async () => {
                          try {
                            await confirm({
                              title: `Are you sure?`,
                              description:
                                "Are you sure you want to link your Square account? Once linked, you will not be able to change the payment processor without contacting support.",
                              confirmationText: "Yes, link my Square account",
                              cancellationText: "No",
                              dialogProps: {
                                sx: {
                                  zIndex: 99999,
                                },
                              },
                            });
                          } catch {
                            return;
                          }

                          setProcessing(true);

                          try {
                            const apiBankAccount = await getData(
                              `/bankaccounts/square/oauthurl`,
                              {
                                objectId: organization.id,
                                accountType: "GameStore",
                              }
                            );

                            if (
                              !apiBankAccount?.url ||
                              !apiBankAccount?.state
                            ) {
                              throw new Error(
                                "There was an error retrieving the Square OAuth URL."
                              );
                            }

                            window.location.assign(apiBankAccount.url);
                          } catch (err) {
                            postNotification("error", err);
                          } finally {
                            setProcessing(false);
                          }
                        }}
                      >
                        Link Bank Account with Square
                      </Button>
                    </Grid>
                  )}

                  {/* Stripe */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "link.stripe",
                        color: "#000",
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "#fff",
                        },
                      }}
                      startIcon={<StripeIcon />}
                      onClick={async () => {
                        try {
                          await confirm({
                            title: `Are you sure?`,
                            description:
                              "Are you sure you want to link your account with Stripe? Once linked, you will not be able to change the payment processor without contacting support.",
                            confirmationText:
                              "Yes, link my account with Stripe",
                            cancellationText: "No",
                            dialogProps: {
                              sx: {
                                zIndex: 99999,
                              },
                            },
                          });
                        } catch {
                          return;
                        }

                        var addParams = `?redirect_uri=${window.location.origin}/stripe/account-creation`;
                        if (organization?.owner) {
                          if (organization.owner.email) {
                            addParams =
                              addParams +
                              "&stripe_user[email]=" +
                              organization.owner.email;
                          }

                          addParams =
                            addParams +
                            "&stripe_user[first_name]=" +
                            organization.owner.firstName;
                          addParams =
                            addParams +
                            "&stripe_user[last_name]=" +
                            organization.owner.lastName;
                        }
                        addParams =
                          addParams +
                          `&state=${
                            organization.id
                          }&client_id=${getEnvironmentVariable(
                            "STRIPE_CLIENT_ID"
                          )}`;
                        window.location.assign(
                          "https://connect.stripe.com/express/oauth/authorize" +
                            addParams
                        );
                      }}
                    >
                      Link Bank Account with Stripe
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>

        {Boolean(processing) && <ModalLoadingFull />}
      </Container>
    </Box>
  );
}
